import { useMemo } from 'react';
import { TASK_STATUSES, type Task, type TaskStatus } from '@allemnii-pulse/shared';
import { useCollection } from '../../lib/firestore.js';
import { useProject } from '../projects/ProjectProvider.js';
import {
  addDays,
  isWeekend,
  scheduleTasks,
  scheduledDates,
  toCalendarSpan,
} from '../../lib/schedule.js';
import { Card } from '../../components/ui/Card.js';
import { UserName } from '../../components/UserName.js';
import { ExportMenu } from '../exports/ExportMenu.js';

// Wide enough that an hour (PX_PER_DAY / 8) is visible — coding-agent tasks are often 1-2 hours.
const PX_PER_DAY = 48;
const MIN_BAR_W = 6;
const ROW_H = 34;
const BAR_H = 20;
const HEADER_H = 42;

const STATUS_COLOR: Record<TaskStatus, string> = {
  backlog: 'var(--st-backlog)',
  in_progress: 'var(--st-in-progress)',
  on_hold: 'var(--st-on-hold)',
  blocked: 'var(--st-blocked)',
  testing: 'var(--st-testing)',
  in_review: 'var(--st-in-review)',
  done: 'var(--st-done)',
  canceled: 'var(--st-canceled)',
};

const STATUS_LABEL: Record<TaskStatus, string> = {
  backlog: 'Backlog',
  in_progress: 'In progress',
  on_hold: 'On hold',
  blocked: 'Blocked',
  testing: 'Testing',
  in_review: 'In review',
  done: 'Done',
  canceled: 'Canceled',
};

function fmt(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * A dependency-derived Gantt view — see lib/schedule.ts. There's no per-task start/due date in
 * the schema (docs/PLAN.md §13 item 6: "Timeline is read-only in v1"), so bars come from
 * forward-scheduling the real dependency graph against each task's estimate, anchored to the
 * project's start date. This is real computed structure, not placeholder positions. Dependency
 * arrows are likewise derived from real data (dependsOn) — nothing here is decorative.
 */
export function TimelinePage() {
  const { project, projectId } = useProject();
  const { data: tasks, loading } = useCollection<Task>(`projects/${projectId}/tasks`);

  const schedule = useMemo(() => scheduleTasks(tasks), [tasks]);
  const sorted = useMemo(
    () =>
      [...tasks].sort(
        (a, b) =>
          (schedule.get(a.id)?.startDayOffset ?? 0) - (schedule.get(b.id)?.startDayOffset ?? 0),
      ),
    [tasks, schedule],
  );
  // Bars are drawn on a calendar axis, so working-day offsets are mapped past weekends.
  const spans = useMemo(() => {
    const map = new Map<string, { start: number; end: number }>();
    if (!project) return map;
    for (const [id, s] of schedule) map.set(id, toCalendarSpan(project.startDate, s));
    return map;
  }, [schedule, project]);
  const totalDays = Math.ceil(Math.max(1, ...Array.from(spans.values()).map((s) => s.end))) + 2;
  const todayOffset = project
    ? Math.round((Date.now() - new Date(`${project.startDate}T00:00:00`).getTime()) / 86400000)
    : null;

  const rowTop = useMemo(() => new Map(sorted.map((t, i) => [t.id, i * ROW_H])), [sorted]);
  const totalHeight = sorted.length * ROW_H;

  const barGeometry = useMemo(() => {
    const map = new Map<string, { left: number; width: number; midY: number }>();
    for (const t of sorted) {
      const s = spans.get(t.id);
      const top = rowTop.get(t.id);
      if (!s || top === undefined) continue;
      map.set(t.id, {
        left: s.start * PX_PER_DAY,
        width: Math.max(MIN_BAR_W, (s.end - s.start) * PX_PER_DAY),
        midY: top + ROW_H / 2,
      });
    }
    return map;
  }, [sorted, rowTop, spans]);

  const arrows = useMemo(() => {
    const list: { id: string; d: string }[] = [];
    for (const t of sorted) {
      const to = barGeometry.get(t.id);
      if (!to) continue;
      for (const depId of t.dependsOn) {
        const from = barGeometry.get(depId);
        if (!from) continue;
        const x1 = from.left + from.width;
        const x2 = to.left;
        const y1 = from.midY;
        const y2 = to.midY;
        const midX = x1 + Math.max(4, Math.min(14, (x2 - x1) / 2));
        list.push({ id: `${depId}->${t.id}`, d: `M ${x1} ${y1} H ${midX} V ${y2} H ${x2}` });
      }
    }
    return list;
  }, [sorted, barGeometry]);

  const weekendDays = useMemo(() => {
    if (!project) return [];
    return Array.from({ length: totalDays }, (_, day) => day).filter((day) =>
      isWeekend(addDays(project.startDate, day)),
    );
  }, [totalDays, project]);

  const weekTicks = useMemo(() => {
    const ticks: { left: number; label: string }[] = [];
    for (let offset = 0; offset < totalDays; offset += 7) {
      ticks.push({
        left: offset * PX_PER_DAY,
        label: project ? fmt(addDays(project.startDate, offset)) : '',
      });
    }
    return ticks;
  }, [totalDays, project]);

  const statusesPresent = TASK_STATUSES.filter((s) => tasks.some((t) => t.status === s));
  const totalWidth = totalDays * PX_PER_DAY;

  if (loading) return <div className="p-4 text-sm text-muted sm:p-6">Loading…</div>;
  if (!project) return null;

  if (tasks.length === 0) {
    return (
      <div className="p-4 sm:p-6">
        <Card className="p-6 text-center text-sm text-muted sm:p-8">
          No tasks yet — generate tasks first.
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
        <p className="text-xs text-muted">
          Computed from task dependencies and estimates, anchored to the project start date (
          {project.startDate}). Not an editable schedule.
        </p>
        <div className="flex flex-wrap items-center gap-3.5 text-[11px] text-muted">
          {statusesPresent.map((s) => (
            <span key={s} className="flex items-center gap-1.5">
              <span
                className="h-2 w-3.5 rounded-[3px]"
                style={{ backgroundColor: STATUS_COLOR[s] }}
              />
              {STATUS_LABEL[s]}
            </span>
          ))}
        </div>
        <ExportMenu projectId={projectId} docType="timeline" className="ml-auto" />
      </div>

      <Card className="flex overflow-hidden">
        {/* task list — narrower on phones so the chart beside it keeps a usable width */}
        <div className="w-36 flex-shrink-0 border-r border-border sm:w-[260px]">
          <div
            className="flex items-center gap-2 border-b border-border bg-surface-2/60 px-3 text-[10.5px] font-semibold uppercase tracking-wide text-muted"
            style={{ height: HEADER_H }}
          >
            <span>Task</span>
            <span className="ml-auto hidden sm:inline">Owner</span>
          </div>
          {sorted.map((task) => (
            <div
              key={task.id}
              className="flex items-center gap-2 border-b border-border/50 px-3 text-xs"
              style={{ height: ROW_H }}
            >
              <span
                className="h-1.5 w-1.5 flex-shrink-0 rounded-sm"
                style={{ backgroundColor: STATUS_COLOR[task.status] }}
              />
              <span className="truncate">{task.title}</span>
              {task.assigneeUid && (
                <UserName
                  uid={task.assigneeUid}
                  avatar
                  hideName
                  // Dropped on phones: the narrow column needs the room for the title.
                  className="ml-auto hidden flex-shrink-0 sm:inline-flex"
                  avatarClassName="h-5 w-5"
                />
              )}
            </div>
          ))}
        </div>

        {/* chart */}
        {/* overscroll-x-contain: a swipe that hits the chart's edge mustn't turn into the
            browser's back/forward gesture on phones. */}
        <div className="min-w-0 flex-1 overflow-x-auto overscroll-x-contain md:overscroll-x-auto">
          <div style={{ width: Math.max(totalWidth, 1), minWidth: '100%' }}>
            {/* scale */}
            <div
              className="relative border-b border-border bg-surface-2/60"
              style={{ height: HEADER_H }}
            >
              {weekTicks.map((tick, i) => (
                <span
                  key={i}
                  className="absolute top-0 flex h-full items-center border-l border-border/60 pl-2 text-[11px] font-semibold first:border-l-0"
                  style={{ left: tick.left }}
                >
                  {tick.label}
                </span>
              ))}
            </div>

            {/* grid + bars */}
            <div className="relative" style={{ height: totalHeight }}>
              {/* weekends — not working days, so no task time is scheduled on them */}
              {weekendDays.map((day) => (
                <span
                  key={`we-${day}`}
                  className="absolute top-0 bg-surface-2/60"
                  style={{ left: day * PX_PER_DAY, width: PX_PER_DAY, height: totalHeight }}
                />
              ))}

              {/* week gridlines */}
              {weekTicks.map((tick, i) => (
                <span
                  key={i}
                  className="absolute top-0 border-l border-border/40"
                  style={{ left: tick.left, height: totalHeight }}
                />
              ))}

              {/* row separators */}
              {sorted.map((task, i) => (
                <div
                  key={`sep-${task.id}`}
                  className="absolute inset-x-0 border-b border-border/40"
                  style={{ top: (i + 1) * ROW_H - 1 }}
                />
              ))}

              {/* dependency arrows */}
              <svg
                width={totalWidth}
                height={totalHeight}
                className="absolute left-0 top-0 overflow-visible"
                fill="none"
              >
                <defs>
                  <marker
                    id="tl-arrow"
                    markerWidth="7"
                    markerHeight="7"
                    refX="5.5"
                    refY="3"
                    orient="auto"
                  >
                    <path d="M0 0 L6 3 L0 6 Z" fill="var(--ink-400)" />
                  </marker>
                </defs>
                {arrows.map((a) => (
                  <path
                    key={a.id}
                    d={a.d}
                    stroke="var(--ink-400)"
                    strokeWidth={1.2}
                    markerEnd="url(#tl-arrow)"
                  />
                ))}
              </svg>

              {/* today marker */}
              {todayOffset !== null && todayOffset >= 0 && todayOffset <= totalDays && (
                <>
                  <div
                    className="absolute top-0 border-l border-dashed border-accent"
                    style={{ left: todayOffset * PX_PER_DAY, height: totalHeight }}
                  />
                  <span
                    className="absolute top-1 flex h-[16px] -translate-x-1/2 items-center rounded-full bg-accent px-2 text-[9px] font-bold text-accent-fg"
                    style={{ left: todayOffset * PX_PER_DAY }}
                  >
                    TODAY
                  </span>
                </>
              )}

              {/* bars */}
              {sorted.map((task, i) => {
                const geo = barGeometry.get(task.id);
                if (!geo) return null;
                const s = schedule.get(task.id)!;
                const { start, end } = project
                  ? scheduledDates(project.startDate, s)
                  : { start: null, end: null };
                return (
                  <div
                    key={task.id}
                    className="absolute flex items-center overflow-hidden text-ellipsis whitespace-nowrap rounded-md px-2 text-[10px] font-semibold text-white shadow-sm"
                    style={{
                      left: geo.left,
                      width: geo.width,
                      top: i * ROW_H + (ROW_H - BAR_H) / 2,
                      height: BAR_H,
                      backgroundColor: STATUS_COLOR[task.status],
                    }}
                    title={
                      start && end
                        ? `${fmt(start)}${end > start ? ` → ${fmt(end)}` : ''} · ${
                            task.estimateHours ?? '?'
                          }h`
                        : undefined
                    }
                  >
                    {geo.width > 60 ? task.title : ''}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
}
